import type { NeuronId, SerialNumber } from "~domain/devices/models/common-device-model.ts";
import type { ModulatorCsId, ModulatorHwId } from "~domain/devices/models/modulators/modulator-model.ts";
import type { PilotCsId, PilotHwId } from "~domain/devices/models/pilot/pilot-model.ts";
import type { SiteExternalId, SiteId } from "~domain/sites/models/site-model.ts";
import type { SubscriberId } from "~domain/subscribers/models/subscriber-model.ts";

import { z } from "zod";

export const siteSearchGeocodedAddressResponse = z.object({
  id: z.number().nullish(),
  administrativeDivision: z.string().array(),
  cityCode: z.string().nullable(),
  country: z
    .object({
      label: z.string(),
      isoCode: z.string(),
    })
    .nullable(),
  externId: z.string().nullish(),
  geocoding: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
    })
    .nullish(),
  inseeCode: z.string().nullable(),
  label: z.string(),
  provider: z.string().nullable(),
  score: z.number().nullable(),
  street: z.string().nullish(),
  streetNumber: z.string().nullish(),
  city: z.string().nullable(),
  zipCode: z.string().nullable(),
});

export const siteSearchPostalAddressResponse = z.object({
  address: z.string().nullable(),
  addressComplement: z.string().nullable(),
  digicode: z.string().nullable(),
  building: z.string().nullable(),
  level: z.number().nullable(),
  stairway: z.string().nullable(),
  cityCode: z.string().nullable(),
  zipCode: z.string().nullable(),
  city: z.string().nullable(),
});

export const backofficeSiteSearchResponseSchema = z.object({
  modulatorSearchInfo: z.object({
    csId: z
      .number()
      .transform((s) => s as ModulatorCsId)
      .nullable(),
    hwId: z
      .number()
      .transform((s) => s as ModulatorHwId)
      .nullable(),
    neuronId: z
      .string()
      .transform((s) => s as NeuronId)
      .nullable(),
    serialNumber: z
      .string()
      .transform((s) => s as SerialNumber)
      .nullable(),
  }),
  pilotSearchInfo: z.object({
    csId: z
      .number()
      .transform((s) => s as PilotCsId)
      .nullable(),
    hwId: z
      .number()
      .transform((s) => s as PilotHwId)
      .nullable(),
  }),
  siteSearchInfo: z.object({
    deliveryPoint: z.string().nullable(),
    externId: z
      .string()
      .transform((s) => s as SiteExternalId)
      .nullable(),
    id: z.number().transform((s) => s as SiteId),
    postalAddress: siteSearchPostalAddressResponse.nullable(),
    geocodedAddress: siteSearchGeocodedAddressResponse.nullable(),
  }),
  subscriberSearchInfo: z
    .object({
      companyName: z.string().nullable(),
      emailList: z.array(z.any()),
      firstname: z.string().nullable(),
      lastname: z.string().nullable(),
      id: z
        .number()
        .transform((s) => s as SubscriberId)
        .nullable(),
      phoneList: z.array(z.any()),
    })
    .nullable(),
});

export type BackofficeSiteSearchResponse = z.infer<typeof backofficeSiteSearchResponseSchema>;
